import React from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, FormControl, Select, MenuItem, CircularProgress, Box, Typography, Chip } from '@material-ui/core';
import { ChevronRight, ExpandMore } from '@material-ui/icons';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { renderStatusChip } from '../utils/helpers';
import { useFeaturesAccess } from '../utils/useFeaturesAccess';
import { fetchProjectDetailsAPI } from '../Stepper/stepperAPI';
import { useProjectDevNav } from '../ProjectDevNavContext';

const TableSection = ({
  classes,
  paginatedProjects,
  viewType, // 'overallView' | 'systemView' - controls Operation column visibility
  selectedProject,
  open,
  toggleExpand,
  actionSelectValues,
  setActionSelectValues,
  selectedBatchNumbers,
  setSelectedBatchNumbers,
  selectedUnitProcedures,
  setSelectedUnitProcedures,
  selectedCampaigns,
  setSelectedCampaigns,
  selectedStages,
  setSelectedStages,
  selectedBatches,
  setSelectedBatches,
  selectedOperations,
  setSelectedOperations,
  loading,
  apiError,
  mfgPlantsList,
}) => {
  const history = useHistory();
  // Full-screen in-app views (Gantt Chart) are opened through this context
  // rather than by pushing a route.
  const { openGanttChart } = useProjectDevNav();

  // Site / Plant column: campaigns.cmoSite is a single string (just the site
  // name, e.g. "HYD") with no separate plant field of its own — mfgPlantsList
  // (already fetched for the Site / Plant filter, FilterSection.jsx) is the only
  // place a plant name lives, keyed by site name. If cmoSite already contains a
  // "/" it's treated as pre-combined and shown as-is (covers the DB genuinely
  // storing "Site / Plant" as one value); otherwise the first plant found under
  // that site name is appended for display only — a site with more than one
  // plant just shows the first.
  const siteToPlant = React.useMemo(() => {
    const map = {};
    (mfgPlantsList || []).forEach((p) => {
      const siteName = (p?.SiteName != null ? String(p.SiteName) : '').trim();
      const plant = (p?.mfgPlantShortName != null ? String(p.mfgPlantShortName) : '').trim();
      if (siteName && plant && !map[siteName]) map[siteName] = plant;
    });
    return map;
  }, [mfgPlantsList]);

  const siteWithPlant = (value) => {
    const site = value != null ? String(value).trim() : '';
    if (!site || site.includes('/')) return site || 'N/A';
    const plant = siteToPlant[site];
    return plant ? `${site} / ${plant}` : site;
  };

  // Screen 26 feature access (fetched in ProjectDevelopment/index.jsx).
  // Admins see all row actions regardless of the stored flags.
  const featuresAccess = useFeaturesAccess();

  // Edit Project fetches the project's details BEFORE navigating, and holds a
  // loader on this row's action control while it does. Navigating first meant
  // the stepper mounted with nothing and painted a form full of "NA" before its
  // own fetch landed and re-rendered the whole page. Prefetching here keeps the
  // wait on the control the user just clicked, and the response is handed to the
  // stepper in navigation state so its first paint already has the data.
  const [editLoadingProjectId, setEditLoadingProjectId] = React.useState(null);
  const isMountedRef = React.useRef(true);
  React.useEffect(() => () => { isMountedRef.current = false; }, []);

  const handleActionChange = async (project, action) => {
    if (action === 'viewGanttChart') {
      // Opens the Gantt Chart as an in-app full-screen view via
      // ProjectDevNavContext — the same URL-less pattern Batch Analytics and
      // Batch Genealogy use. (It used to be a deliberate no-op because the
      // screen did not exist and no `productdevelopment/gantt-chart/:id` route
      // was registered, so pushing one landed the user on a 404.)
      //
      // The chart is portfolio-wide by design (FSD: Portfolio View is the
      // default landing view showing all Projects), so the clicked row's
      // project is not passed as a scope — the user drills from the portfolio.
      openGanttChart();
      return;
    }
    if (action === 'editProject') {
      // Pass the campaign identity explicitly. The dashboard row has no
      // campaign id at its top level (it lives inside projectDetails.campaigns),
      // and the update payload needs collabrixCampaignID + collabrixProjectID so
      // corda can match-and-update the right campaign.
      const editCampaign = project.projectDetails?.campaigns?.[0];
      const collabrixProjectID = project.collabrixProjectID ?? project.id ?? null;
      const target = `/pcmp/${localStorage.collabType}/${localStorage.roleName}/${localStorage.organizationName}/${localStorage.ecosystemId}/productdevelopment/add-new-project`;
      const navState = {
        editProject: true,
        projectData: project,
        collabrixProjectID,
        collabrixCampaignID:
          editCampaign?.collabrixCampaignID ?? project.collabrixCampaignID ?? null,
      };

      // Reset the dropdown now — the loader below is what shows progress.
      setActionSelectValues((prev) => ({ ...prev, [project.id]: "" }));

      // No id to fetch with: go straight there and let the stepper deal with it,
      // exactly as before.
      if (!collabrixProjectID) {
        history.push(target, navState);
        return;
      }

      setEditLoadingProjectId(project.id);
      let prefetched = null;
      try {
        prefetched = await fetchProjectDetailsAPI(collabrixProjectID);
      } catch (err) {
        // Deliberately non-blocking: on a failed prefetch we still navigate and
        // let the stepper run its own fetch (and surface its own error) rather
        // than stranding the user on the dashboard with no feedback.
        console.error('Edit Project prefetch failed; the stepper will fetch it instead:', err);
      }
      if (!isMountedRef.current) return;
      setEditLoadingProjectId(null);
      history.push(
        target,
        prefetched ? { ...navState, prefetchedProjectDetails: prefetched } : navState
      );
      return;
    } else if (action === 'editCampaign') {
      history.push(
        `/pcmp/${localStorage.collabType}/${localStorage.roleName}/${localStorage.organizationName}/${localStorage.ecosystemId}/productdevelopment/add-new-project`,
        {
          editProject: true,
          projectData: project,
          editCampaign: true,
        }
      );
    }
    setActionSelectValues(prev => ({
      ...prev,
      [project.id]: ""
    }));
  };

  return (
    <TableContainer 
      component={Paper} 
      className={classes.tableContainer}
      style={{
        borderTop: '1px solid #C6C6C6',
        borderRight: '1px solid #C6C6C6',
        borderBottom: '1px solid #C6C6C6',
        borderLeft: '1px solid #C6C6C6',
        padding: '1px',
        paddingRight: '2px',
        marginBottom: '10px',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <Table>
        <TableHead>
          {/* UI-PD-Changes: column layout follows the approved collapsed /
              expanded designs.
              Collapsed: CMO | Site | Project ID / Product Description | PO No. |
                         Campaign ID | Contracted Qty. | UoM | Campaign Status |
                         Start Date | Expected End Date | Action
              Expanded:  CMO | Site | Project ID | PO No. | Campaign ID |
                         CMO Stage (Chemical No.) | Product Batch No. |
                         Batch Start Date | Batch End Date | Batch Status */}
          <TableRow style={{ backgroundColor: '#F9F9FC' }}>
            <TableCell align="left" className={`${classes.headerCell} ${classes.col1to5}`} style={{ paddingRight: '3px' }} />
            <TableCell align="left" className={`${classes.headerCell} ${classes.cmoColumn}`} style={{ paddingLeft: '3px' }}>CMO</TableCell>
            <TableCell align="left" className={`${classes.headerCell} ${classes.col1to5}`}>Site / Plant</TableCell>
            <TableCell align="left" className={`${classes.headerCell} ${classes.col1to5}`}>
              {selectedProject
                ? 'Project ID'
                : (
                  <>
                    Project ID /
                    <br />
                    Product Description
                  </>
                )}
            </TableCell>
            <TableCell align="left" className={`${classes.headerCell} ${classes.col1to5}`}>PO No.</TableCell>
            <TableCell align="left" className={`${classes.headerCell} ${classes.col1to5}`}>Campaign ID</TableCell>
            {selectedProject ? (
              <>
                <TableCell align="left" className={`${classes.headerCell} ${classes.col1to5}`}>CMO Stage (Chemical No.)</TableCell>
                <TableCell align="left" className={`${classes.headerCell} ${classes.col1to5}`}>Product Batch No.</TableCell>
                <TableCell align="left" className={`${classes.headerCell} ${classes.col1to5}`}>Batch Start Date</TableCell>
                <TableCell align="left" className={`${classes.headerCell} ${classes.col1to5}`}>Batch End Date</TableCell>
                <TableCell align="center" className={`${classes.headerCell} ${classes.col7}`}>Batch Status</TableCell>
              </>
            ) : (
              <>
                <TableCell align="right" className={`${classes.headerCell} ${classes.col1to5}`}>Contracted Qty.</TableCell>
                <TableCell align="left" className={`${classes.headerCell} ${classes.col1to5}`}>UoM</TableCell>
                <TableCell align="center" className={`${classes.headerCell} ${classes.col7}`}>Campaign Status</TableCell>
                <TableCell align="left" className={`${classes.headerCell} ${classes.col6}`}>Start Date</TableCell>
                <TableCell align="left" className={`${classes.headerCell} ${classes.col6}`}>Expected End Date</TableCell>
                <TableCell align="center" className={`${classes.headerCell} ${classes.col8to10}`}>Action</TableCell>
              </>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell
                colSpan={selectedProject ? 11 : 12}
                align="center"
                style={{ padding: '40px' }}
              >
                <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <CircularProgress color="primary" thickness={7} />
                </Box>
              </TableCell>
            </TableRow>
          ) : apiError ? (
            <TableRow>
              <TableCell
                colSpan={selectedProject ? 11 : 12}
                align="center"
                style={{ padding: '40px' }}
              >
                <Box style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
                  <Typography variant="body1" style={{ color: '#d32f2f', fontWeight: 500 }}>
                    {apiError}
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : paginatedProjects.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={selectedProject ? 11 : 12}
                align="center"
                style={{ padding: '40px' }}
              >
                <Box style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
                  <Typography variant="body1" style={{ color: '#666', fontWeight: 500 }}>
                    No data available
                  </Typography>
                  <Typography variant="body2" style={{ color: '#999' }}>
                    No projects found matching the current filters.
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            paginatedProjects.map((project) => {
            if (selectedProject && project.id !== selectedProject) {
              return null;
            }

            // Precompute dropdown defaults & options (same pattern as campaign/stage)
            const firstCampaign = project.projectDetails?.campaigns?.[0];
            const firstStage = firstCampaign?.stages?.[0];
            const firstBatch = firstStage?.batches?.[0];

            const selectedCampaignId =
              selectedCampaigns[project.id] || firstCampaign?.campaignId || '';
            const campaign = project.projectDetails?.campaigns?.find(
              (c) => c.campaignId === selectedCampaignId
            );

            const selectedStageId =
              selectedStages[project.id] || campaign?.stages?.[0]?.stageId || '';
            const stage = campaign?.stages?.find((s) => s.stageId === selectedStageId);

            const selectedBatchNumber =
              selectedBatches[project.id]?.batchNumber || stage?.batches?.[0]?.batchNumber || '';
            const batch = stage?.batches?.find((b) => b.batchNumber === selectedBatchNumber);

            const operationsFromAPI = Array.isArray(batch?.operations) ? batch.operations : [];
            const selectedOperation = selectedOperations[project.id] || 'All';

            const campaigns = project.projectDetails?.campaigns || [];
            const stages = campaign?.stages || [];
            const batches = stage?.batches || [];

            const handleCampaignChange = (e) => {
              const newCampaignId = e.target.value;
              setSelectedCampaigns(prev => ({
                ...prev,
                [project.id]: newCampaignId
              }));

              const newCampaign = campaigns.find(c => c.campaignId === newCampaignId);
              const firstStage = newCampaign?.stages?.[0] || null;
              // Always resync stage + batch to the newly selected campaign.
              setSelectedStages(prev => ({
                ...prev,
                [project.id]: firstStage?.stageId || ''
              }));
              const firstBatch = firstStage?.batches?.[0] || null;
              // Set the first batch, or CLEAR it when the new campaign/stage has
              // none — otherwise the old batch lingers and the tabs keep showing
              // the previous campaign's data.
              setSelectedBatches(prev => ({
                ...prev,
                [project.id]: firstBatch
                  ? { batchNumber: firstBatch.batchNumber, batchId: firstBatch.batchId }
                  : null
              }));
              setSelectedOperations(prev => ({
                ...prev,
                [project.id]: 'All'
              }));
            };

            const handleStageChange = (e) => {
              const newStageId = e.target.value;
              setSelectedStages(prev => ({
                ...prev,
                [project.id]: newStageId
              }));

              const newStage = stages.find(s => s.stageId === newStageId);
              const firstBatch = newStage?.batches?.[0] || null;
              // Set the stage's first batch, or clear it when the stage has none.
              setSelectedBatches(prev => ({
                ...prev,
                [project.id]: firstBatch
                  ? { batchNumber: firstBatch.batchNumber, batchId: firstBatch.batchId }
                  : null
              }));
              setSelectedOperations(prev => ({
                ...prev,
                [project.id]: 'All'
              }));
            };

            const handleBatchChange = (e) => {
              const selectedBatchNumber = e.target.value;
              const batch = batches.find(b => b.batchNumber === selectedBatchNumber);
              
              if (batch) {
                setSelectedBatches(prev => ({
                  ...prev,
                  [project.id]: {
                    batchNumber: batch.batchNumber,
                    batchId: batch.batchId
                  }
                }));
                
                setSelectedOperations(prev => ({
                  ...prev,
                  [project.id]: 'All'
                }));
              }
            };

            const handleOperationChange = (e) => {
              setSelectedOperations(prev => ({
                ...prev,
                [project.id]: e.target.value
              }));
            };

            return (
              <React.Fragment key={project.id}>
                {/* Keep table border consistent when chevron is clicked – rely on default MUI row borders */}
                <TableRow className={classes.tableRow}>
                  <TableCell align="left" className={`${classes.tableCellBody} ${classes.col1to5}`} style={{ paddingRight: '3px' }}>
                    <IconButton
                      size="small"
                      onClick={() => toggleExpand(project.id, project)}
                      style={{ padding: '4px', marginRight: '0' }}
                    >
                      {open[project.id] && selectedProject !== null ? (
                        <ExpandMore style={{ fontSize: '1.2rem', transition: 'transform 0.3s ease', color: '#000000' }} />
                      ) : (
                        <ChevronRight style={{ fontSize: '1.2rem', transition: 'transform 0.3s ease', color: '#000000' }} />
                      )}
                    </IconButton>
                  </TableCell>
                  {/* CMO + Site. Prefer explicit fields; else split the combined
                      cmoSite. cmoSite may be a NUMBER (org id), so coerce to a
                      string before calling any string method. */}
                  <TableCell align="left" className={`${classes.tableCellBody} ${classes.cmoColumn}`} style={{ paddingLeft: '3px' }}>
                    {project.cmoName
                      || (String(project.cmoSite || '').includes(' - ')
                        ? String(project.cmoSite).split(' - ')[0].trim()
                        : (project.cmoSite != null && project.cmoSite !== '' ? project.cmoSite : 'N/A'))}
                  </TableCell>
                  <TableCell align="left" className={`${classes.tableCellBody} ${classes.col1to5}`}>
                    {/* Site / Plant → campaign-level cmoSite (e.g. "PatheonTRO" / "Unit-IV"),
                        enriched with its plant name via siteWithPlant (see above). */}
                    {siteWithPlant(project.cmoSite || project.site)}
                  </TableCell>
                  <TableCell align="left" className={`${classes.tableCellBody} ${classes.col1to5} ${classes.projectIdColumn}`}>
                    {selectedProject ? (
                      // Opened state: only Project ID
                      // (productCode-sponsorProjectID-cmoProductCode), or blank
                      project.projectDisplayId || ''
                    ) : (
                      // Closed state: Project ID / Product Description. When the
                      // product description is absent, drop the "/" separator and
                      // show only the Project ID (blank if that is empty too).
                      <>
                        {project.projectDisplayId || ''}
                        {project.productDescription ? (
                          <>
                            {' /'}
                            <br />
                            {project.productDescription}
                          </>
                        ) : null}
                      </>
                    )}
                  </TableCell>
                  {/* PO No. — value intentionally blanked for now; restore
                      {project.poNumber} once the PO mapping is finalised. */}
                  <TableCell align="left" className={`${classes.tableCellBody} ${classes.col1to5}`} />
                  {/* Campaign ID — dropdown when expanded, plain text when collapsed */}
                  <TableCell align="left" className={`${classes.tableCellBody} ${classes.col1to5}`}>
                    {selectedProject ? (
                      <FormControl variant="outlined" size="small" fullWidth>
                        <Select value={selectedCampaignId} onChange={handleCampaignChange} style={{ height: '32px', fontSize: '14px' }}>
                          {campaigns.map((camp) => (
                            <MenuItem key={camp.campaignId} value={camp.campaignId}>{camp.campaignId}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (project.campaignId || 'N/A')}
                  </TableCell>

                  {selectedProject ? (
                    <>
                      {/* CMO Stage (Chemical No.) */}
                      <TableCell align="left" className={`${classes.tableCellBody} ${classes.col1to5}`}>
                        <FormControl variant="outlined" size="small" fullWidth>
                          <Select value={selectedStageId} onChange={handleStageChange} style={{ height: '32px', fontSize: '14px' }} disabled={stages.length === 0}>
                            {stages.map((st) => (
                              <MenuItem key={st.stageId} value={st.stageId}>{st.stageName || st.stageId}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      {/* Product Batch No. */}
                      <TableCell align="left" className={`${classes.tableCellBody} ${classes.col1to5}`}>
                        <FormControl variant="outlined" size="small" fullWidth>
                          <Select value={selectedBatchNumber} onChange={handleBatchChange} style={{ height: '32px', fontSize: '14px' }} disabled={batches.length === 0}>
                            {batches.map((bt) => (
                              <MenuItem key={bt.batchNumber} value={bt.batchNumber}>{bt.batchNumber}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      {/* Batch Start Date — field: batch.batchStartDate (fallback batch.startDate) */}
                      <TableCell align="left" className={`${classes.tableCellBody} ${classes.col1to5}`}>
                        {(() => {
                          const d = batch?.batchStartDate || batch?.startDate;
                          return d ? moment(d).format('DD-MMM-YYYY').toUpperCase() : 'N/A';
                        })()}
                      </TableCell>
                      {/* Batch End Date — field: batch.batchEndDate (fallback batch.endDate) */}
                      <TableCell align="left" className={`${classes.tableCellBody} ${classes.col1to5}`}>
                        {(() => {
                          const d = batch?.batchEndDate || batch?.endDate;
                          return d ? moment(d).format('DD-MMM-YYYY').toUpperCase() : 'N/A';
                        })()}
                      </TableCell>
                      {/* Batch Status — field: batch.batchStatus (fallback project.batchStatus) */}
                      <TableCell align="center" className={`${classes.tableCellBody} ${classes.col7}`}>
                        {renderStatusChip(batch?.batchStatus || project.batchStatus, classes)}
                      </TableCell>
                    </>
                  ) : (
                    <>
                      {/* Contracted Qty. — field: project.contractedQty
                          (campaign productQuantityRequired). */}
                      <TableCell align="right" className={`${classes.tableCellBody} ${classes.col1to5}`}>
                        {project.contractedQty != null && project.contractedQty !== '' ? project.contractedQty : 'N/A'}
                      </TableCell>
                      {/* UoM — field: project.uom */}
                      <TableCell align="left" className={`${classes.tableCellBody} ${classes.col1to5}`}>
                        {project.uom || 'N/A'}
                      </TableCell>
                      {/* Campaign Status */}
                      <TableCell align="center" className={`${classes.tableCellBody} ${classes.col7}`}>
                        {renderStatusChip(project.campaignStatus, classes)}
                      </TableCell>
                      {/* Start Date — field: project.startDate */}
                      <TableCell align="left" className={`${classes.tableCellBody} ${classes.col6}`}>
                        {project.startDate ? moment(project.startDate).format('DD-MMM-YYYY').toUpperCase() : 'N/A'}
                      </TableCell>
                      {/* Expected End Date — field: project.expectedEndDate */}
                      <TableCell align="left" className={`${classes.tableCellBody} ${classes.col6}`}>
                        {project.expectedEndDate ? moment(project.expectedEndDate).format('DD-MMM-YYYY').toUpperCase() : 'N/A'}
                      </TableCell>
                      {/* Action */}
                      <TableCell align="center" className={`${classes.tableCellBody} ${classes.col8to10}`}>
                        {editLoadingProjectId === project.id ? (
                          // Holds the wait on the control that was clicked; the
                          // page is only opened once its data has arrived.
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            style={{ gap: '8px' }}
                          >
                            <CircularProgress size={18} />
                            <Typography variant="body2">Loading…</Typography>
                          </Box>
                        ) : (
                        <FormControl variant="outlined" size="small" className={classes.actionSelect}>
                          <Select
                            value={actionSelectValues[project.id] || ""}
                            displayEmpty
                            onChange={(e) => handleActionChange(project, e.target.value)}
                            renderValue={(selected) => {
                              if (!selected) {
                                return <span style={{ fontWeight: 500 }}>Choose Action</span>;
                              }
                              return selected;
                            }}
                            MenuProps={{
                              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                              transformOrigin: { vertical: 'top', horizontal: 'left' },
                              getContentAnchorEl: null,
                            }}
                          >
                            {featuresAccess?.actionViewGanttChart && (
                              <MenuItem value="viewGanttChart">
                                View Gantt Chart
                              </MenuItem>
                            )}
                            {featuresAccess?.actionEditProject && (
                              <MenuItem value="editProject">Edit Project</MenuItem>
                            )}
                            {/* Edit Campaign option removed per PD feedback — no longer needed.
                                Handler branch kept in handleActionChange for easy restore. */}
                          </Select>
                        </FormControl>
                        )}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              </React.Fragment>
            );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TableSection;

